<script setup lang="ts">
/**
 * `App.vue` — root mount point.
 *
 * Hosts `<RouterView>` so the route map in `src/router/` decides which
 * top-level view renders (login vs editor). Authenticated initialization
 * is handled by the router's `beforeEach` guard before the first paint;
 * we just render whichever route resolved.
 */
import { RouterView } from "vue-router";
import SystemOverlayHost from "@components/panels/system/SystemOverlayHost.vue";
import FrameRateStatsView from "@components/panels/system/FrameRateStatsView.vue";
import ConfirmationHost from "@components/dialogs/ConfirmationHost.vue";
import LoadProjectProgressView from "@components/dialogs/LoadProjectProgressView.vue";

defineOptions({ name: "App" });
</script>

<template>
  <RouterView />
  <FrameRateStatsView />
  <SystemOverlayHost />
  <!-- Web stand-in for Unity's additive `ViewNames.Confirmation` navigation:
       one mount above every route so any caller can request a dialog. -->
  <ConfirmationHost />
  <!-- Mounted ABOVE the routes, not inside the editor, so a project loads
       under the overlay while the projects grid is still on screen and the
       editor is only entered once there is something in it. `ProjectsView`
       owns that sequencing; this is just the one surface that can span both
       routes. The scene-load and unload overlays stay inside `ProjectView` —
       those belong to a project that is already open. -->
  <LoadProjectProgressView />
</template>

<style>
html, body, #app {
  height: 100%;
  width: 100%;
  overflow: hidden;
}

/* The phone editor shell's bottom cluster — the tool row, the scene row and the
 * drawer — sits ~12px above this box's bottom edge, while `--cf-panel-shadow`
 * reaches ~40px past each of them (24px offset + half of a 48px blur − 12px
 * spread, at `--cf-m-scale`). `overflow: hidden` therefore sliced every one of
 * those shadows off in a hard line just under the pill.
 *
 * Plain `visible`, NOT `clip` + `overflow-clip-margin`: the margin was the
 * tidier answer (it bounds how far ink may escape and never makes the box a
 * scroller) but its support is the newest thing in this file, and where it is
 * missing `overflow: clip` degrades to exactly the `hidden` it replaced — a
 * silent no-op. Nothing inside the phone shell overflows this box in LAYOUT
 * terms anyway: the header, scene row, tool row and drawer are all inset by
 * `--cf-m-gutter`, and the one strip that can outgrow its slot
 * (`.cf-mscenerow__face--options`) is `max-width: 100%` with its own
 * `overflow: hidden`. So there is no scroll range to hand the document, which
 * in the Safari full-bleed tab mode IS the scroller (`useFullbleedTab.ts`).
 *
 * `:has()` rather than a media query so the small-screen breakpoint keeps living in
 * one place (`K_SMALL_SCREEN_QUERY` in `useMobileLayout.ts`), and so every other
 * route and the desktop shell keep clipping — the desktop column-collapse
 * slide in `ProjectView.vue` depends on it.
 *
 * `html` and `body` are deliberately left alone. In this shell `body` is the
 * next clipping box, but its bottom edge is off-screen in every mode that has
 * one (non-full-bleed `100vh` is iOS's LARGE viewport; in full-bleed `body`
 * is already `overflow: visible`) — and setting `overflow` on `body` while the
 * root is `visible` PROPAGATES to the viewport, which would kill the
 * full-bleed scroll pin outright. */
#app:has(.cf-project--mobile) {
  overflow: visible;
}
</style>
